/**
 * /app/$tenantSlug/posh/certificates — My certificates.
 *
 * Page text follows the POSH course language (English / हिन्दी); the
 * certificate PDF itself is always English. The PDF saves under the
 * server's Content-Disposition filename (saveBlobAs), and the blob URL
 * is revoked after a delay so Safari/Firefox still find it.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Award, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { localize, poshApi, toApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { saveBlobAs } from "@/lib/save-blob";
import { useCertificates, useTrainingModules } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TrainingErrorAlert } from "@/components/posh/training-error-alert";
import { poshText, type PoshTextKey } from "@/components/posh/posh-ui-text";
import { usePoshLang } from "@/lib/posh-lang";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { CertificateState } from "@/types/posh";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/certificates",
)({
  component: CertificatesRoute,
});

const CERT_STATE_TEXT: Record<CertificateState, PoshTextKey> = {
  valid: "statusValid",
  expired: "statusExpired",
  superseded: "statusReplaced",
  revoked: "statusRevoked",
};

function CertsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="mt-2 h-4 w-1/3" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-9 w-40" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// The shared en-IN formatter (lib/format): India date for an instant, same
// "5 Nov 2026" shape as every other page.
function fmtDate(iso: string): string {
  return formatDate(iso);
}

function CertificatesList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const certsQ = useCertificates();
  const modulesQ = useTrainingModules();
  const [downloading, setDownloading] = useState<number | null>(null);
  const { lang } = usePoshLang();

  const moduleTitle = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of modulesQ.data?.modules ?? []) {
      map.set(m.module.id, localize(m.module.title, lang) || m.module.code);
    }
    return map;
  }, [modulesQ.data, lang]);

  if (certsQ.isLoading) return <CertsSkeleton />;

  if (certsQ.isError) {
    return (
      <TrainingErrorAlert
        title={poshText("loadCertsError", lang)}
        error={certsQ.error}
        lang={lang}
        onRetry={() => void certsQ.refetch()}
      />
    );
  }

  const certs = [...(certsQ.data?.certificates ?? [])].sort(
    (a, b) =>
      new Date(b.issued_at).getTime() - new Date(a.issued_at).getTime(),
  );

  if (certs.length === 0) {
    return (
      <Card lang={lang}>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Award className="h-6 w-6" aria-hidden />
          </span>
          <p className="max-w-sm text-sm text-muted-foreground">
            {poshText("certsEmpty", lang)}
          </p>
          <Button asChild variant="outline" className="min-h-11 sm:min-h-10">
            <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
              {poshText("goToTraining", lang)}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const onDownload = async (id: number) => {
    setDownloading(id);
    try {
      const blob = await poshApi.downloadCertificatePdf(id);
      // Server's filename when it sends one; revoke happens on a timer.
      saveBlobAs(blob, `workright-certificate-${id}.pdf`);
    } catch (err) {
      const e = toApiError(err);
      toast.error(
        e.error === "certificate_revoked"
          ? poshText("certRevokedError", lang)
          : lang === "en"
            ? e.message
            : poshText("downloadError", lang),
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-3" lang={lang}>
      {certs.map((c) => {
        // A retired module (SH105, migration 166) is not in the module
        // list; the certificate carries its own label for that case.
        const title =
          moduleTitle.get(c.module_id) ??
          (localize(c.module_title, lang) || c.module_code || c.certificate_number);
        // The PUBLIC verification page lives on the marketing site; the
        // server builds the URL from PUBLIC_SITE_URL (the one the PDF/QR
        // prints) — never guess the host.
        const verifyHref = c.verify_url;
        // Annual refresher (migration 121): an older backend sends no
        // `state`, so fall back to the revoked flag.
        const state: CertificateState =
          c.state ?? (c.is_revoked ? "revoked" : "valid");
        const lapsed = state === "expired" || state === "superseded";
        const lapsedLine =
          state === "superseded"
            ? c.lapsed_at
              ? poshText("certReplacedOn", lang, { date: fmtDate(c.lapsed_at) })
              : poshText("certReplaced", lang)
            : c.lapsed_at
              ? poshText("certLapsedOn", lang, { date: fmtDate(c.lapsed_at) })
              : poshText("certLapsed", lang);
        return (
          <Card key={c.id} className={lapsed ? "opacity-80" : undefined}>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Award className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <span className="truncate">{title}</span>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {c.certificate_number} ·{" "}
                    {poshText("issuedOn", lang, { date: fmtDate(c.issued_at) })}
                    {c.valid_until
                      ? ` · ${poshText("validUntil", lang, { date: fmtDate(c.valid_until) })}`
                      : ""}
                  </CardDescription>
                  {lapsed && (
                    <p className="mt-1 text-xs text-amber-800">
                      {lapsedLine} {poshText("certRecordNote", lang)}
                    </p>
                  )}
                </div>
                <StatusBadge
                  status={state === "superseded" ? "expired" : state}
                  label={poshText(CERT_STATE_TEXT[state], lang)}
                />
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                onClick={() => void onDownload(c.id)}
                disabled={c.is_revoked || downloading === c.id}
                aria-label={poshText("downloadPdfAria", lang, { title })}
                className="min-h-11 gap-1.5 sm:min-h-10"
              >
                {downloading === c.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-4 w-4" aria-hidden />
                )}
                {poshText("downloadPdf", lang)}
              </Button>
              {verifyHref && (
                <Button asChild variant="outline" className="min-h-11 gap-1.5 sm:min-h-10">
                  <a href={verifyHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    {poshText("publicVerification", lang)}
                  </a>
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function CertificatesRoute() {
  const { lang } = usePoshLang();
  return (
    <PoshModuleShell
      title={poshText("certsTitle", lang)}
      description={poshText("certsDescription", lang)}
    >
      <PoshFeatureGate>
        <CertificatesList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
